import React, { useEffect, useState, useRef, useMemo } from 'react';
import A4 from '.';

function breakPageContent(children, childRefs, parentRefs) {
  const parentHeight = parentRefs.current[0]?.clientHeight - 150;
  let totalHeight = 0;
  let currentPage = [];
  const newContentParents = [];

  children.forEach((child, index) => {
    const clientHeight = childRefs?.current[index]?.clientHeight || 0;

    if (totalHeight + clientHeight > parentHeight) {
      newContentParents.push(currentPage);
      currentPage = [];
      totalHeight = 0;
    }

    currentPage.push(child);
    totalHeight += clientHeight;
  });

  newContentParents.push(currentPage);

  return newContentParents;
}

/**
 * SectionTemplate wraps sections in a root section that always starts on a new page.
 * If children provided to this component exceed the height of a page, they are resorted onto different pages.
 * This array is referred to as contentParents.
 * The ResizeObserver is subscribed to each child and when a height updates, contentParents are re-computed
 *
 * @param {Object} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child elements to be included in the section.
 * @param {string} props.id - The unique identifier of the section.
 * @param {string} props.name - The name of the section.
 * @param {Object} [props.metadata={}] - Metadata for the section.
 * @param {boolean} [props.hasHeader=true] - Flag to determine if the section should have a header.
 * @param {boolean} [props.hasFooter=true] - Flag to determine if the section should have a footer.
 *
 * @returns {React.Element} A div containing A4.Section with A4.Page(s), headers, footers and children wrapped inside.
 *
 * @example
 *  <SectionTemplate id='section1' name='Introduction' metadata={{ site_id: '123', company: 'ABC Corp', company_logo: 'logo.png', report_name: 'Annual Report' }}>
 *    <Paragraph>Introductory text...</Paragraph>
 *    <Paragraph>More text that overflows to another page...</Paragraph>
 *  </SectionTemplate>
 */
export default function SectionTemplate({
  children,
  id,
  name,
  metadata = {},
  hasHeader = true,
  hasFooter = true,
}) {
  const parentRefs = useRef([]);
  const childRefs = useRef([]);

  const { current: pageTemplateId } = useRef(Math.random().toString(36).slice(2));

  const [contentParents, setContentParents] = useState([[]]);

  const { site_id, company, company_logo, report_name } = metadata;
  const infoHeader = { site_id, company, company_logo };
  const infoFooter = { company, report_name };

  const nextChildren = useMemo(() => {
    // Convert children to an array and filter out null or undefined values
    let childArray = React.Children.toArray(children).filter(Boolean);
    // Map over the children and create the child elements
    return childArray.map((child, index) => (
      <div
        key={`content-child-${index}`}
        id={child.props?.id ?? `content-child-${index}`}
        name={child.props?.name ?? ''}
        ref={(el) => {
          childRefs.current[index] = el;
        }}
      >
        {child}
      </div>
    ));
  }, [children]);

  const updatePages = () => {
    const newPages = breakPageContent(nextChildren, childRefs, parentRefs);
    setContentParents(newPages);
  };
  // Store previously observed elements
  const prevChildRefs = useRef([]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(updatePages);

    childRefs.current.forEach((ref, index) => {
      if (ref instanceof Element && ref !== prevChildRefs.current[index]) {
        // If the child is new, observe it
        resizeObserver.observe(ref);
        // If there was a previous child, unobserve it
        if (prevChildRefs.current[index] instanceof Element) {
          resizeObserver.unobserve(prevChildRefs.current[index]);
        }
        // Update the reference to the current child
        prevChildRefs.current[index] = ref;
      }
    });

    return () => {
      // On cleanup, unobserve all current children
      childRefs.current.forEach((ref) => {
        if (ref instanceof Element) {
          resizeObserver.unobserve(ref);
        }
      });
    };
  }, [nextChildren]);

  useEffect(() => {
    if (nextChildren.length > 0) {
      updatePages();
    }
  }, [parentRefs.current[0]?.clientHeight, children]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <A4.Section id={id} name={name}>
        {contentParents.map((content, index) => (
          <A4.Page
            key={index}
            id={id}
            name={`${pageTemplateId}${index ? `-overflow-${index}` : ''}`}
          >
            {hasHeader && <A4.Header info={infoHeader} />}
            <div
              id={`parent-${index}`}
              ref={(el) => {
                parentRefs.current[index] = el;
              }}
              style={styles.heightFull}
            >
              {content.map((child, index) => (
                <A4.Section
                  key={`section-child-${index}`}
                  id={child.props.id}
                  name={child.props.name}
                >
                  {child}
                </A4.Section>
              ))}
            </div>
            {hasFooter && <A4.Footer info={infoFooter} />}
          </A4.Page>
        ))}
      </A4.Section>
    </div>
  );
}

const styles = {
  heightFull: {
    height: '100%',
    overflowY: 'hidden',
    position: 'relative',
  },
};
